// hooks/useProfileBonus.js
// Hook to check and award profile completion bonus
// ============================================
// Add this to ProfilePage to automatically award bonus
// when user completes their profile
// ============================================

import { useState, useEffect, useCallback } from 'react';
import { awardProfileCompletionBonus, getBonusStatus, isProfileComplete } from '../services/bonusService';

/**
 * Hook to manage profile completion bonus
 * @param {object} user - Current user object
 * @returns {object} - { checkAndAwardBonus, bonusAwarded, loading }
 */
export const useProfileBonus = (user) => {
  const [bonusAwarded, setBonusAwarded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);

  // Check if bonus already claimed on mount
  useEffect(() => {
    const checkBonusStatus = async () => {
      if (!user) return;
      
      try {
        const result = await getBonusStatus();
        if (result.success && result.bonuses?.profileCompletionBonusAwarded) {
          setBonusAwarded(true);
        }
      } catch (error) {
        console.error('Error checking bonus status:', error);
      }
      setChecked(true);
    };
    
    checkBonusStatus();
  }, [user]);

  // Check profile and award bonus if complete
  const checkAndAwardBonus = useCallback(async (profileData) => {
    if (bonusAwarded || loading) {
      return { success: false, alreadyAwarded: true };
    }

    // Check if profile is complete
    const complete = isProfileComplete(profileData || user);
    if (!complete) {
      return { success: false, incomplete: true };
    }

    setLoading(true);
    
    try {
      const result = await awardProfileCompletionBonus();
      
      if (result.success) {
        setBonusAwarded(true);
        return { 
          success: true, 
          tokensAwarded: result.tokensAwarded,
          newBalance: result.newBalance 
        };
      } else if (result.alreadyClaimed) {
        setBonusAwarded(true);
        return { success: false, alreadyAwarded: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('Error awarding profile bonus:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, [user, bonusAwarded, loading]);

  return {
    checkAndAwardBonus,
    bonusAwarded,
    loading,
    checked
  };
};

export default useProfileBonus;